import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { NAV_LINKS, BRAND } from '../../lib/constants';
import { subscribeNewsletter } from '../../lib/api';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [state, setState] = useState({ loading: false, msg: '' });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setState({ loading: true, msg: '' });
    try {
      const res = await subscribeNewsletter(email);
      setState({
        loading: false,
        msg: res.status === 'subscribed' ? 'Welcome to the ritual.' : 'You are already with us.',
      });
      setEmail('');
    } catch {
      setState({ loading: false, msg: 'Please enter a valid email.' });
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#7C9CB1] text-[#F4F8F9] border-t border-[#7C9CB1]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-24 pb-12 grid grid-cols-1 md:grid-cols-12 gap-12">
        {/* Newsletter */}
        <div className="md:col-span-6">
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F8F9]/70 mb-4">
            <span className="label-divider" />The Letter
          </p>
          <h2 className="font-serif text-4xl md:text-5xl text-[#F4F8F9] leading-tight font-light max-w-lg">
            Quiet correspondence. Once a moon.
          </h2>
          <form
            onSubmit={onSubmit}
            data-testid="newsletter-form"
            className="mt-8 max-w-md flex items-end gap-3 border-b border-[#F4F8F9]/40 pb-3"
          >
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              data-testid="newsletter-email-input"
              className="flex-1 bg-transparent text-[#F4F8F9] placeholder-[#F4F8F9]/50 text-base font-light tracking-wide focus:outline-none"
            />
            <button
              type="submit"
              disabled={state.loading}
              data-testid="newsletter-submit-button"
              className="text-[#F4F8F9] hover:text-[#C1D7E2] transition-colors disabled:opacity-50"
            >
              <ArrowUpRight size={20} strokeWidth={1.5} />
            </button>
          </form>
          {state.msg && (
            <p
              data-testid="newsletter-status"
              className="mt-3 text-sm text-[#F4F8F9]/90 font-light"
            >
              {state.msg}
            </p>
          )}
        </div>

        {/* Nav */}
        <div className="md:col-span-3">
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F8F9]/70 mb-4">
            <span className="label-divider" />Wander
          </p>
          <ul className="space-y-3">
            {NAV_LINKS.map((l) => (
              <li key={l.path}>
                <Link
                  to={l.path}
                  data-testid={`footer-nav-${l.label.toLowerCase().replace(/\s+/g, '-')}-link`}
                  className="font-serif text-xl text-[#F4F8F9] hover:text-[#C1D7E2] transition-colors"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Brand info */}
        <div className="md:col-span-3">
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F8F9]/70 mb-4">
            <span className="label-divider" />The House
          </p>
          <Link
            to="/"
            data-testid="footer-logo-link"
            aria-label="Shreesavaa — home"
            className="inline-block mb-5"
          >
            <img
              src={BRAND.logoUrl}
              alt="Shreesavaa"
              data-testid="footer-logo-image"
              className="h-14 w-auto object-contain"
              style={{
                filter:
                  'drop-shadow(0 2px 6px rgba(0,0,0,0.45))',
              }}
            />
          </Link>
          <p className="text-[#F4F8F9]/85 font-light leading-relaxed">
            Shreesavaa is a luxury skincare house pairing pure, traceable ingredients
            with on-chain provenance and a tokenised loyalty ritual.
          </p>
          <a
            href={`mailto:${BRAND.email}`}
            data-testid="footer-brand-email"
            className="mt-5 inline-block font-mono text-xs tracking-[0.18em] text-[#F4F8F9]/85 hover:text-[#C1D7E2] transition-colors"
          >
            {BRAND.email}
          </a>
          <p className="mt-6 font-mono text-[10px] tracking-[0.25em] text-[#F4F8F9]/60">
            {BRAND.established} · HEAVENER · OKLAHOMA
          </p>
        </div>
      </div>

      <div className="border-t border-[#F4F8F9]/20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#F4F8F9]/60">
            © {new Date().getFullYear()} {BRAND.name} — All rituals reserved.
          </p>
          <p
            data-testid="footer-3k-seal"
            className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#F4F8F9]/65 text-center md:text-right leading-relaxed"
          >
            <span className="text-[#F4F8F9]">K¹</span>&nbsp;Kewra · the Signal &nbsp;·&nbsp;
            <span className="text-[#F4F8F9]">K²</span>&nbsp;Kamal · the State &nbsp;·&nbsp;
            <span className="text-[#F4F8F9]">K³</span>&nbsp;Karma · the Record
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
